import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MonthSelector, { currentMonthKey } from "@/components/MonthSelector";
import {
  FileUp,
  FileText,
  Download,
  Trash2,
  CheckCircle2,
  Package,
  Loader2,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminRdaPage() {
  const [rdas, setRdas] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewRda, setViewRda] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [uploadMes, setUploadMes] = useState(currentMonthKey());

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: u }] = await Promise.all([
        api.get("/rda-monthly"),
        api.get("/admin/users"),
      ]);
      setRdas(r);
      setClients(u.filter((x) => x.role === "client"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!selectedClient) {
      toast.error("Selecciona un cliente");
      return;
    }
    if (!file) {
      toast.error("Selecciona un archivo RDA (.xlsx)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mes", uploadMes);
    setUploading(true);
    try {
      const { data } = await api.post(
        `/admin/clients/${selectedClient}/rda`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        `RDA subido: ${data.total_rows} fila(s) · ${data.delivered_rows} entregada(s)`
      );
      setOpenUpload(false);
      setFile(null);
      setSelectedClient("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const download = async (m) => {
    try {
      const res = await api.get(
        `/admin/clients/${m.user_id}/rda-monthly/${m.mes}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RDA_${m.mes.replace("-", "")}_${m.id_cliente || m.razon_social || "cliente"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al descargar");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/rda/${id}`);
      toast.success("Subida eliminada");
      load();
      // Refresh open detail if any
      if (viewRda) {
        openDetail({ user_id: viewRda.user_id, mes: viewRda.mes });
      }
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const openDetail = async (m) => {
    setViewLoading(true);
    try {
      const { data } = await api.get(
        `/admin/clients/${m.user_id}/rda-monthly/${m.mes}`
      );
      setViewRda({ ...data, razon_social: m.razon_social, id_cliente: m.id_cliente });
    } catch {
      toast.error("Error al cargar detalle");
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Administración · Entregas"
          title="RDA — Confirmaciones de entrega"
          description="Sube el archivo RDA (Excel) a cada cliente para confirmar la entrega de sus productos. El cliente podrá descargarlo desde su portal."
          actions={
            <button
              onClick={() => setOpenUpload(true)}
              data-testid="rda-upload-btn"
              className="flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors"
            >
              <FileUp className="w-4 h-4" />
              Subir RDA
            </button>
          }
        />

        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B1B3D] text-white">
              <tr>
                <Th>Cliente</Th>
                <Th>Mes</Th>
                <Th className="text-right">Subidas</Th>
                <Th className="text-right">Filas únicas</Th>
                <Th className="text-right">Entregadas</Th>
                <Th className="text-right">Bultos</Th>
                <Th className="text-right">Unidades</Th>
                <Th>Comunas</Th>
                <Th>Última actualización</Th>
                <Th className="text-right">Acciones</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={10} className="p-10 text-center text-slate-500">
                    Cargando...
                  </td>
                </tr>
              )}
              {!loading && rdas.length === 0 && (
                <tr>
                  <td colSpan={10} className="p-10 text-center text-slate-500">
                    Aún no se han subido archivos RDA.
                  </td>
                </tr>
              )}
              {rdas.map((m) => {
                const key = `${m.user_id}-${m.mes}`;
                return (
                <tr
                  key={key}
                  data-testid={`rda-month-${key}`}
                  className="hover:bg-slate-50"
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0B1B3D]">
                      {m.razon_social || m.user_email}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      {m.user_email}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openDetail(m)}
                      data-testid={`rda-view-${key}`}
                      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-wider bg-[#D95A40]/10 text-[#D95A40] font-bold px-2 py-1 hover:bg-[#D95A40] hover:text-white transition-colors"
                    >
                      <FileText className="w-3 h-3" />
                      {m.mes}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-bold">
                      {m.upload_count}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">
                    {m.total_rows}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {m.delivered_rows === m.total_rows ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs">
                        <CheckCircle2 className="w-3 h-3" />
                        {m.delivered_rows}
                      </span>
                    ) : (
                      <span>{m.delivered_rows}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {(m.total_bultos ?? 0).toLocaleString("es-CL")}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {(m.total_unidades ?? 0).toLocaleString("es-CL")}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {(m.comunas || []).slice(0, 2).join(", ")}
                    {(m.comunas?.length || 0) > 2
                      ? ` +${m.comunas.length - 2}`
                      : ""}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                    {m.latest_upload_at
                      ? new Date(m.latest_upload_at).toLocaleString("es-CL")
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => download(m)}
                        data-testid={`rda-download-${key}`}
                        className="p-2 bg-[#0B1B3D] hover:bg-[#D95A40] text-white transition-colors"
                        title="Descargar RDA mensual unificado"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload dialog */}
      <Dialog open={openUpload} onOpenChange={setOpenUpload}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tighter">
              Subir archivo RDA
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                Cliente destino
              </label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger data-testid="rda-select-client">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.razon_social || c.name} — {c.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                Mes correspondiente <span className="text-[#D95A40]">*</span>
              </label>
              <MonthSelector
                value={uploadMes}
                onChange={setUploadMes}
                testid="rda-mes-selector"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                Archivo RDA (Excel .xlsx)
              </label>
              <input
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="rda-file-input"
                className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#0B1B3D] file:text-white file:font-bold file:uppercase file:tracking-wider file:text-xs hover:file:bg-[#D95A40] file:cursor-pointer border border-slate-300 py-1"
              />
              {file && (
                <div className="text-xs text-slate-600 mt-2">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 p-3">
              <strong className="text-[#0B1B3D]">Columnas esperadas:</strong>{" "}
              Punto de entrega, Nombre, Dirección, Comuna, Región, Documento de
              entrega, SAP, Bultos, Unidades, Estado, Recepcionista, Fecha
              entrega.{" "}
              <span className="text-slate-400">(GD y Lote — opcionales)</span>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => {
                setOpenUpload(false);
                setFile(null);
              }}
              className="px-4 py-2 border border-slate-300 text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={uploading || !file || !selectedClient}
              data-testid="rda-submit-btn"
              className="flex items-center gap-2 px-5 py-2 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 text-white text-sm font-bold uppercase tracking-wider"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Subiendo...
                </>
              ) : (
                <>
                  <FileUp className="w-4 h-4" /> Subir
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View detail dialog */}
      <Dialog open={!!viewRda} onOpenChange={(v) => !v && setViewRda(null)}>
        <DialogContent className="bg-white sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tighter">
              RDA {viewRda?.mes} — {viewRda?.razon_social || viewRda?.user_email}
            </DialogTitle>
            <div className="text-sm text-slate-600">
              {viewRda?.upload_count} subida(s) fusionada(s) · {viewRda?.total_rows} filas únicas ·{" "}
              {viewRda?.delivered_rows} entregadas
            </div>
          </DialogHeader>

          {/* Upload history */}
          {viewRda?.sources?.length > 0 && (
            <div className="border border-slate-200 bg-slate-50 p-3 mb-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 mb-2">
                Historial de subidas del mes ({viewRda.sources.length})
              </div>
              <ul className="space-y-1.5">
                {viewRda.sources.map((s) => (
                  <li
                    key={s.id}
                    data-testid={`rda-source-${s.id}`}
                    className="flex items-center justify-between gap-3 text-xs bg-white border border-slate-200 px-3 py-1.5"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-3.5 h-3.5 text-[#D95A40] shrink-0" />
                      <span className="font-mono text-[#0B1B3D] truncate">
                        {s.filename}
                      </span>
                      <span className="text-slate-400 shrink-0">
                        · {s.total_rows} filas
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-slate-500 whitespace-nowrap">
                        {new Date(s.uploaded_at).toLocaleString("es-CL")}
                      </span>
                      <a
                        href="#download"
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const res = await api.get(`/rda/${s.id}/download`, {
                              responseType: "blob",
                            });
                            const url = URL.createObjectURL(res.data);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = s.filename || "rda.xlsx";
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            URL.revokeObjectURL(url);
                          } catch {
                            toast.error("Error al descargar");
                          }
                        }}
                        className="text-[#0B1B3D] hover:text-[#D95A40]"
                        title="Descargar archivo original"
                        data-testid={`rda-source-download-${s.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            className="text-slate-400 hover:text-red-600"
                            data-testid={`rda-source-delete-${s.id}`}
                            title="Eliminar esta subida"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar esta subida?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Se eliminará &laquo;{s.filename}&raquo; del historial. El RDA
                              mensual se recalculará con las subidas restantes.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(s.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="overflow-auto max-h-[50vh] border border-slate-200">
            <RdaRowsTable rows={viewRda?.rows || []} />
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

export function RdaRowsTable({ rows }) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-slate-100 sticky top-0">
        <tr>
          <Th2>#</Th2>
          <Th2>Nombre</Th2>
          <Th2>Dirección</Th2>
          <Th2>Comuna</Th2>
          <Th2 className="text-right">Región</Th2>
          <Th2>Doc entrega</Th2>
          <Th2 className="text-right">GD</Th2>
          <Th2 className="text-right">SAP</Th2>
          <Th2>Lote</Th2>
          <Th2 className="text-right">Bultos</Th2>
          <Th2 className="text-right">Unidades</Th2>
          <Th2>Estado</Th2>
          <Th2>Recepcionista</Th2>
          <Th2>Fecha</Th2>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 bg-white">
        {rows.map((r, i) => (
          <tr key={i} className="hover:bg-slate-50">
            <td className="px-2 py-1.5 tabular-nums">
              {r["Punto de entrega"]}
            </td>
            <td className="px-2 py-1.5 font-semibold text-[#0B1B3D]">
              {r["Nombre"]}
            </td>
            <td className="px-2 py-1.5 text-slate-700">{r["Dirección"]}</td>
            <td className="px-2 py-1.5 text-[#D95A40] font-semibold">
              {r["Comuna"]}
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums">
              {r["Región"]}
            </td>
            <td className="px-2 py-1.5 tabular-nums font-mono">
              {r["Documento de entrega"]}
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums font-mono text-[#D95A40] font-semibold">
              {r["GD"] || "—"}
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums">{r["SAP"]}</td>
            <td className="px-2 py-1.5 font-mono uppercase text-[#0B1B3D]">
              {r["Lote"] || "—"}
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
              {r["Bultos"]}
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums">
              {r["Unidades"]}
            </td>
            <td className="px-2 py-1.5">
              {r._delivered ? (
                <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-bold uppercase">
                  <CheckCircle2 className="w-3 h-3" />
                  {r["Estado"]}
                </span>
              ) : (
                <span className="bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-bold uppercase">
                  {r["Estado"] || "—"}
                </span>
              )}
            </td>
            <td className="px-2 py-1.5">{r["Recepcionista"]}</td>
            <td className="px-2 py-1.5 text-xs text-slate-600 whitespace-nowrap">
              {r["Fecha entrega"]
                ? new Date(r["Fecha entrega"]).toLocaleDateString("es-CL")
                : "—"}
            </td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={12} className="p-6 text-center text-slate-500">
              Sin filas
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.15em] ${className}`}
    >
      {children}
    </th>
  );
}
function Th2({ children, className = "" }) {
  return (
    <th
      className={`px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600 ${className}`}
    >
      {children}
    </th>
  );
}
